// Back-to-top button: appears after the visitor scrolls down, scrolls smoothly to the top
// (instant for prefers-reduced-motion) and moves keyboard focus to the top of the page.
class SBackToTop extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.threshold = Number(this.dataset.threshold) || 400;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.toggle(window.scrollY > this.threshold);
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    // Stay clear of the Shopify Inbox chat button (or any other app chat launcher) in the same corner.
    this.baseBottom = 96; // fallback clearance when no launcher is detected
    this.gap = 12;
    this.avoid = () => this.avoidChat();
    window.addEventListener('resize', this.avoid, { passive: true });
    this.avoidTimer = setInterval(this.avoid, 1000);
    this.avoid();
    this.toggle(window.scrollY > this.threshold);

    this.button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      const target = document.body;
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.avoid);
    clearInterval(this.avoidTimer);
  }

  chatRect() {
    const selectors = [
      '#shopify-chat',
      'inbox-online-store-chat',
      '[id*="shopify-chat"]',
      '[id*="ShopifyChat"]',
      'iframe[title*="chat" i]',
      'iframe[id*="chat" i]',
      'button[aria-label*="chat" i]',
    ];
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let best = null;
    const candidates = new Set(document.querySelectorAll(selectors.join(',')));
    // App launchers are usually fixed elements appended to <body>; include those too.
    Array.from(document.body.children).forEach((element) => {
      if (element !== this && getComputedStyle(element).position === 'fixed') candidates.add(element);
    });
    document.querySelectorAll('#shopify-chat *').forEach((element) => {
      if (element.tagName === 'IFRAME' || element.tagName === 'BUTTON') candidates.add(element);
    });
    candidates.forEach((element) => {
      if (this.contains(element)) return;
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      // Only the launcher sitting in the bottom-right corner matters; skip an open chat panel taller than half the screen.
      const inCorner = rect.right > viewportWidth - 160 && rect.bottom > viewportHeight - 200;
      if (!inCorner || rect.height > viewportHeight / 2) return;
      if (!best || rect.top < best.top) best = rect;
    });
    return best;
  }

  // True when the element (or a host/ancestor, crossing shadow roots) is fixed-positioned and isn't this button.
  isForeignFixed(element) {
    let node = element;
    while (node && node !== document.documentElement) {
      if (node === this) return false;
      if (node.nodeType === 1 && getComputedStyle(node).position === 'fixed') return true;
      node = node.parentElement || (node.getRootNode && node.getRootNode().host) || null;
    }
    return false;
  }

  // Probe the bottom-right corner column for any fixed overlay (chat launchers often sit in iframes
  // or shadow DOM that selectors can't reach). Returns the highest y it occupies, or null.
  probeTop() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const xs = [width - 24, width - 40, width - 64];
    let top = null;
    for (let offset = 4; offset <= 320; offset += 6) {
      const y = height - offset;
      if (y <= 0) break;
      for (const x of xs) {
        const hit = document.elementsFromPoint(x, y).find((element) => !this.contains(element));
        if (hit && this.isForeignFixed(hit)) {
          top = y;
          break;
        }
      }
    }
    return top;
  }

  avoidChat() {
    const rect = this.chatRect();
    const probed = this.probeTop();
    let chatTop = rect ? rect.top : null;
    if (probed !== null && (chatTop === null || probed < chatTop)) chatTop = probed;
    const fallback = window.innerWidth >= 768 ? 120 : this.baseBottom;
    let bottom = fallback;
    if (chatTop !== null) bottom = Math.max(this.baseBottom, window.innerHeight - chatTop + this.gap);
    if (bottom !== this.currentBottom) {
      this.currentBottom = bottom;
      this.style.bottom = `calc(${bottom}px + env(safe-area-inset-bottom, 0px))`;
    }
  }

  toggle(show) {
    if (show === this.shown) return;
    this.shown = show;
    this.toggleAttribute('data-visible', show);
    if (this.reducedMotion.matches) this.button.style.transition = 'none';
    this.button.style.opacity = show ? '1' : '0';
    this.button.style.transform = show ? 'translateY(0)' : 'translateY(12px)';
    this.button.style.pointerEvents = show ? 'auto' : 'none';
    this.button.tabIndex = show ? 0 : -1;
    this.button.setAttribute('aria-hidden', show ? 'false' : 'true');
  }
}

if (!customElements.get('s-back-to-top')) customElements.define('s-back-to-top', SBackToTop);
